import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { FlexContainer, SurveyTemplateParser } from '@act/storybook';
import { Box } from '@mui/material';
import { Control, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Prompt from './Prompt';
import SurveyInput from './inputs/SurveyInput';
import type { SurveyFormData } from './inputs/types';
import StepActions from './StepActions';
import { OPTION_BASED_QUESTIONS } from './constants/question.types';
import { generateQuestionResponseSchema } from '@act/shared/validators';
import {
  buildSurveyQuestionResponseSyncKey,
  getSurveyQuestionFormValue,
  type SurveyQuestionFormValue,
} from './surveyQuestionForm.util';

export interface QuestionProps {
  question: SurveyV2.QuestionDto;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  member: UserDto;
  numberOfSteps: number;
  activeStep: number;
  questionResponse: SurveyV2.QuestionResponseDto;
  /** In-progress value restored when remounting after Previous / step-header nav. */
  draftValue?: SurveyQuestionFormValue;
  handleChange: (answer: SurveyV2.AnswerQuestionDto) => Promise<boolean>;
  handleStep: (step: number | null, checkpointId?: number) => void;
  onFormStateChange?: (
    isDirty: boolean,
    draftValue?: SurveyQuestionFormValue
  ) => void;
}

const Question = ({
  question,
  assignedSurvey,
  member,
  questionResponse,
  activeStep,
  numberOfSteps,
  draftValue,
  handleChange,
  handleStep,
  onFormStateChange,
}: QuestionProps) => {
  const [prompt, setPrompt] = useState<SurveyV2.PromptDto | null>(null);
  /** Capture once for remount restore; cleared after apply so response sync cannot re-stomp. */
  const draftOnMountRef = useRef(draftValue);
  /** Skip the initial clean mount report so parent-held dirty/draft survive remount. */
  const hasReportedDirtyRef = useRef(false);

  const responseSyncKey = useMemo(
    () => buildSurveyQuestionResponseSyncKey(questionResponse),
    [questionResponse]
  );

  // Depend on responseSyncKey (content), not questionResponse identity — Redux
  // feed/websocket patches often replace the graph with equivalent answers.
  const questionResponseRef = useRef(questionResponse);
  questionResponseRef.current = questionResponse;

  const savedValue = useMemo(
    () =>
      getSurveyQuestionFormValue(question.type, questionResponseRef.current),
    [question.type, responseSyncKey]
  );

  const validationSchema = useMemo(() => {
    return generateQuestionResponseSchema(
      question.type,
      question.validationConfig || {}
    );
  }, [question.type, question.validationConfig]);

  type FormData = z.infer<typeof validationSchema>;

  const { control, getValues, reset, setValue, formState } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    // Defaults match the saved response so a restored draft marks the form dirty.
    defaultValues: { value: savedValue },
  });

  const { isDirty } = formState;

  const { isValid } = formState;

  const watchedValue = useWatch({ control, name: 'value' });

  useEffect(() => {
    if (isDirty) {
      hasReportedDirtyRef.current = true;
      onFormStateChange?.(
        true,
        watchedValue as SurveyQuestionFormValue
      );
      return;
    }
    if (hasReportedDirtyRef.current) {
      hasReportedDirtyRef.current = false;
      onFormStateChange?.(false);
    }
  }, [isDirty, watchedValue, onFormStateChange]);

  useEffect(() => {
    reset({ value: savedValue });
    const draft = draftOnMountRef.current;
    if (draft !== undefined) {
      // shouldValidate: remounted drafts must re-run resolver so Next enables
      // when the restored value is already valid (shouldDirty alone is not enough).
      setValue('value', draft as FormData['value'], {
        shouldDirty: true,
        shouldValidate: true,
      });
      draftOnMountRef.current = undefined;
    }
    // Omit questionResponse identity — only re-sync when persisted content changes
    // (responseSyncKey / savedValue). Otherwise equivalent Redux replacements reset
    // after draftOnMountRef was cleared and wipe in-progress input + dirty tracking.
  }, [question.id, responseSyncKey, question.type, savedValue, reset, setValue]);

  const togglePrompt = useCallback(
    (prompt: SurveyV2.PromptDto) => {
      if (prompt) {
        setPrompt(prompt);
      } else {
        setPrompt(question.prompt);
      }
    },
    [question.prompt]
  );

  useEffect(() => {
    togglePrompt(question.prompt);
  }, [question.prompt, togglePrompt]);

  const handleStepChange = useCallback(
    async (step: number): Promise<void> => {
      const value = getValues().value;
      if (step > activeStep) {
        const success = await handleChange(
          getAnswerFromValue(question, assignedSurvey.id, value)
        );
        if (success) {
          // Clear dirty/draft before unmount so active-step-only mounting does
          // not leave a stale unsaved guard after a successful save.
          hasReportedDirtyRef.current = false;
          onFormStateChange?.(false);
          // Snap to latest step after responses update (null => steps.length - 1).
          // Advancing by a fixed index races Redux and can leave the UI on the
          // answered question until the dialog is reopened.
          handleStep(null);
        }
      } else {
        // Previous: keep parent dirty + draft so remount restores in-progress edits.
        handleStep(step);
      }
    },
    [
      getValues,
      activeStep,
      handleChange,
      question,
      assignedSurvey.id,
      handleStep,
      onFormStateChange,
    ]
  );

  return (
    <FlexContainer direction="column" gap={1} height="auto">
      <Box>
        <SurveyTemplateParser
          template={question.text}
          member={member}
          assignedSurvey={assignedSurvey}
        />
      </Box>
      <Box margin="8px 4px 0 0" padding="8px 12px 8px 0">
        <SurveyInput
          control={control as Control<SurveyFormData>}
          name="value"
          type={question.type}
          options={question.options}
          validationConfig={question.validationConfig || {}}
          togglePrompt={togglePrompt}
        />
      </Box>
      <Box>
        {prompt && (
          <Prompt
            prompt={prompt}
            assignedSurvey={assignedSurvey}
            member={member}
          />
        )}
      </Box>
      <StepActions
        activeStep={activeStep}
        numberOfSteps={numberOfSteps}
        disableNext={!isValid}
        handleStep={handleStepChange}
      />
    </FlexContainer>
  );
};

export default Question;

const isEmpty = value => !value || (Array.isArray(value) && value.length === 0);

const getAnswerFromValue = (
  question: SurveyV2.QuestionDto,
  assignedSurveyId: number,
  value: string | number[] | null | undefined
): SurveyV2.AnswerQuestionDto => {
  let normalizedValue: string | number[] | null | undefined = value;

  if (
    normalizedValue &&
    OPTION_BASED_QUESTIONS.includes(question.type) &&
    !Array.isArray(normalizedValue)
  ) {
    normalizedValue = [parseInt(String(normalizedValue), 10)];
  }

  if (Array.isArray(normalizedValue)) {
    normalizedValue = normalizedValue.map(optionId => Number(optionId));
  }

  return {
    questionId: question.id,
    assignedSurveyId: assignedSurveyId,
    ...(isEmpty(normalizedValue)
      ? { skipped: true }
      : Array.isArray(normalizedValue)
        ? { selectedOptionIds: normalizedValue }
        : { textResponse: normalizedValue }),
  };
};
